/**
 * Профиль курьера: точка сборки и язык.
 *
 * Точка сборки — то место, откуда курьер забирает заказы. В расчётах она пока
 * не участвует (маршруты и порядок объезда — после MVP, docs/09-roadmap.md),
 * но администратору нужна уже сейчас, а курьеру — возможность её увидеть и
 * поправить.
 */
import { Suspense, lazy, useState } from 'react';
import { LOCALES, type CurrentUser, type Locale } from '@arbuz/shared';
import type { LatLon } from '@arbuz/ui-map';
import { ApiError, api } from '../api';
import { makeT } from '../i18n';
import { haptic } from '../tg';
import { dangerButton, inputStyle, labelStyle, secondaryButton } from './courierUi';

/** Карта весит заметно больше экрана и нужна не при каждом заходе. */
const MapPicker = lazy(async () => {
  await import('@arbuz/ui-map/styles.css');
  const m = await import('@arbuz/ui-map');
  return { default: m.MapPicker };
});

export function CourierProfile({ user, onBack }: { user: CurrentUser; onBack: () => void }) {
  const [locale, setLocale] = useState<Locale>(user.locale);
  const t = makeT(locale);

  const [point, setPoint] = useState<LatLon | null>(
    user.pickup_lat !== null && user.pickup_lon !== null
      ? { lat: user.pickup_lat, lon: user.pickup_lon }
      : null,
  );
  const [address, setAddress] = useState(user.pickup_address_text ?? '');
  const [showMap, setShowMap] = useState(false);
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function patch(body: Record<string, unknown>) {
    setBusy(true);
    setError(null);
    try {
      await api.patch<CurrentUser>('/api/profile', body);
      setSaved(true);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('actionFailed'));
    } finally {
      setBusy(false);
    }
  }

  /** Координаты идут парой — этого требует и контракт, и ограничение в БД. */
  async function savePickup() {
    haptic('light');
    await patch({
      pickup_lat: point?.lat ?? null,
      pickup_lon: point?.lon ?? null,
      pickup_address_text: address.trim() ? address.trim() : null,
    });
  }

  async function clearPickup() {
    haptic('warning');
    setPoint(null);
    setAddress('');
    await patch({ pickup_lat: null, pickup_lon: null, pickup_address_text: null });
  }

  async function changeLocale(next: Locale) {
    setLocale(next);
    setSaved(false);
    await patch({ locale: next });
  }

  return (
    <main className="page">
      <h1 className="h1">{t('courierProfile')}</h1>

      <section className="card">
        <p style={{ margin: 0 }}>
          <strong>{user.first_name ?? t('courierProfile')}</strong>
        </p>
        {user.phone && (
          <p className="muted" style={{ margin: 'var(--s-1) 0 0', fontSize: 'var(--fs-sm)' }}>
            {user.phone}
          </p>
        )}
      </section>

      {/* ── Язык ─────────────────────────────────────────────────────────── */}
      <section style={{ marginTop: 'var(--s-6)' }}>
        <h2 style={{ fontSize: 'var(--fs-h2)', lineHeight: 'var(--lh-h2)', margin: '0 0 var(--s-3)' }}>
          {t('languageLabel')}
        </h2>
        <div style={{ display: 'flex', gap: 'var(--s-2)' }}>
          {LOCALES.map((l) => (
            <button
              key={l}
              type="button"
              aria-pressed={locale === l}
              disabled={busy}
              onClick={() => void changeLocale(l)}
              style={{
                minHeight: 'var(--tap-min)',
                padding: '0 var(--s-5)',
                borderRadius: 'var(--r-full)',
                border: `1px solid ${locale === l ? 'var(--c-primary-text)' : 'var(--c-border-input)'}`,
                background: locale === l ? 'var(--c-primary-subtle)' : 'var(--c-surface)',
                color: locale === l ? 'var(--c-primary-text)' : 'var(--c-text)',
                font: 'inherit',
                cursor: 'pointer',
              }}
            >
              {l === 'ru' ? 'Русский' : 'Oʻzbekcha'}
            </button>
          ))}
        </div>
      </section>

      {/* ── Точка сборки ─────────────────────────────────────────────────── */}
      <section style={{ marginTop: 'var(--s-6)' }}>
        <h2 style={{ fontSize: 'var(--fs-h2)', lineHeight: 'var(--lh-h2)', margin: '0 0 var(--s-2)' }}>
          {t('pickupTitle')}
        </h2>
        <p className="muted" style={{ fontSize: 'var(--fs-sm)', margin: '0 0 var(--s-3)' }}>
          {t('pickupHint')}
        </p>

        <div style={{ marginBottom: 'var(--s-3)' }}>
          <label htmlFor="pickup-address" style={labelStyle}>
            {t('pickupAddressLabel')}
          </label>
          <input
            id="pickup-address"
            value={address}
            onChange={(e) => {
              setAddress(e.target.value);
              setSaved(false);
            }}
            maxLength={500}
            style={inputStyle}
          />
        </div>

        <button
          type="button"
          style={secondaryButton}
          onClick={() => {
            haptic('light');
            setShowMap((v) => !v);
          }}
        >
          {showMap ? t('hideMap') : t('pickOnMap')}
        </button>

        {showMap && (
          <div style={{ marginTop: 'var(--s-3)' }}>
            <Suspense fallback={<p className="muted">{t('mapLoading')}</p>}>
              <MapPicker
                value={point}
                onChange={(p) => {
                  setPoint(p);
                  setSaved(false);
                }}
              />
            </Suspense>
          </div>
        )}

        <p className="muted" style={{ fontSize: 'var(--fs-sm)', margin: 'var(--s-3) 0 0' }}>
          {point
            ? `${point.lat.toFixed(5)}, ${point.lon.toFixed(5)}`
            : t('pickupNotSet')}
        </p>

        {error && (
          <p
            role="alert"
            style={{ color: 'var(--c-danger)', fontSize: 'var(--fs-sm)', marginTop: 'var(--s-2)' }}
          >
            {error}
          </p>
        )}

        {saved && !error && (
          <p
            role="status"
            style={{
              color: 'var(--c-primary-text)',
              fontSize: 'var(--fs-sm)',
              marginTop: 'var(--s-2)',
            }}
          >
            {t('pickupSaved')}
          </p>
        )}

        <div style={{ display: 'grid', gap: 'var(--s-2)', marginTop: 'var(--s-4)' }}>
          <button
            type="button"
            className="btn-primary"
            disabled={busy}
            data-loading={busy ? 'true' : undefined}
            onClick={() => void savePickup()}
          >
            {t('savePickup')}
          </button>

          {(point || address) && (
            <button type="button" style={dangerButton} disabled={busy} onClick={() => void clearPickup()}>
              {t('clearPickup')}
            </button>
          )}

          <button type="button" style={secondaryButton} onClick={onBack}>
            {t('courierDeliveries')}
          </button>
        </div>
      </section>
    </main>
  );
}
